<script lang="ts" setup>
import { switchToVerticalNavOnLtOverlayNavBreakpoint } from '@layouts/utils'
import { useUserStore } from '@core/stores/user'
import { userIsManager } from '@/utils/helpers'
import { App } from '@/types/generated'
import UserData = App.Http.Controllers.Auth.UserData

const DefaultLayoutWithVerticalNav = defineAsyncComponent(() => import('./components/DefaultLayoutWithVerticalNav.vue'))
const userStore = useUserStore()
const { t } = useI18n()
const route = useRoute()

const systemErrors = useCookie<any>('systemErrors')
const showContactSupportErrorMessage = ref<boolean>(systemErrors?.value?.showContactSupportErrorMessage)

useCookie('systemErrors').value = null

// ℹ️ This will switch to vertical nav when define breakpoint is reached when in horizontal nav layout
// Remove below composable usage if you are not using horizontal nav layout in your app
switchToVerticalNavOnLtOverlayNavBreakpoint()

const { layoutAttrs, injectSkinClasses } = useSkins()

injectSkinClasses()

// Check for manager notification message
const userData = useCookie<UserData>('userData')
const organizationSettings = useCookie<any>('organizationSettings')

const fetchMaintenanceStatus = async () => {
  try {
    const response = await fetch('/api/maintenance/status', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })

    if (!response.ok)
      return

    const result = await response.json()

    userStore.meetingsAvailable = result?.meetingsAvailable !== false

    if (result?.notificationMessage) {
      userStore.maintenanceNotificationText = result.notificationMessage
      userStore.maintenanceNotification = true
    }
    else {
      userStore.maintenanceNotification = false
    }
  }
  catch {
    // Silently ignore — maintenance status is non-critical
  }
}

onMounted(async () => {
  if (userData.value && organizationSettings.value) {
    const userRoles: string[] = userData.value?.roles ?? []
    if (userIsManager(userRoles) && organizationSettings.value?.notificationMessage) {
      userStore.newNotificationText = organizationSettings.value.notificationMessage
      userStore.newNotification = true
    }
  }

  if (userData.value)
    await fetchMaintenanceStatus()
})

watch(() => route.path, async () => {
  if (userData.value)
    await fetchMaintenanceStatus()
})
</script>

<template>
  <VSnackbar
    v-model="userStore.maintenanceNotification"
    transition="scroll-y-reverse-transition"
    location="top center"
    color="warning"
    variant="flat"
    :timeout="15000"
    width="400"
  >
    <div class="justify-start d-flex align-center">
      <VIcon
        icon="tabler-alert-triangle"
        size="24"
      />
      <div
        class="d-flex flex-column ml-3"
        style="max-width: 330px"
      >
        <p
          v-dompurify-html="userStore.maintenanceNotificationText || t('maintenance-page.games_blocked_message')"
          class="mb-0 text-white text-subtitle-2"
        />
      </div>
    </div>
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="userStore.maintenanceNotification = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="userStore.chatNewMessage"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="primary"
    variant="flat"
    timeout="10000"
    width="300"
  >
    <div class="justify-start d-flex align-center">
      <VIcon
        icon="tabler-message-circle"
        size="24"
      />
      <div
        class="d-flex flex-column ml-3 overflow-hidden"
        style="max-width: 230px"
      >
        <p
          class="text-h6 mb-0 text-white text-truncate"
          style="font-weight: 600"
        >
          От: {{ userStore.chatNewMessageFrom }}
        </p>
        <p class="mb-0 text-white text-subtitle-2 text-truncate">
          {{ userStore.chatNewMessageText }}
        </p>
      </div>
    </div>
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="userStore.chatNewMessage = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="userStore.newNotification"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="primary"
    variant="flat"
    timeout="10000"
    width="300"
  >
    <div class="justify-start d-flex align-center">
      <VIcon
        icon="tabler-info-circle"
        size="24"
      />
      <div
        class="d-flex flex-column ml-3"
        style="max-width: 230px"
      >
        <p
          v-dompurify-html="userStore.newNotificationText"
          class="mb-0 text-white text-subtitle-2"
        />
      </div>
    </div>
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="userStore.newNotification = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="userStore.responseHasError"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="error"
  >
    {{ t('error.default') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="userStore.responseHasError = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="showContactSupportErrorMessage"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="error"
  >
    {{ t('error.contact-support') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="showContactSupportErrorMessage = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="userStore.forbidden"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="error"
  >
    {{ t('error.forbidden') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="userStore.forbidden = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <Component
    v-bind="layoutAttrs"
    :is="DefaultLayoutWithVerticalNav"
  />
</template>

<style lang="scss">
// As we are using `layouts` plugin we need its styles to be imported
@use "@layouts/styles/default-layout";
</style>
